import * as React from "react";
import { useState, useEffect, useCallback, useRef } from "react";
import {
  makeStyles,
  shorthands,
  Button,
  Input,
  Dropdown,
  Option,
  Field,
  Card,
  Text,
  MessageBar,
  MessageBarBody,
  Combobox,
  Tag,
  TagGroup,
  Badge,
  Checkbox,
} from "@fluentui/react-components";
import { DocumentTextRegular, DismissRegular, AddRegular, SearchRegular, CalendarRegular } from "@fluentui/react-icons";
import { EmailFormData } from "../types/tender";
import { tagService, TagWithCount } from "../services/tagService";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("16px"),
  },
  card: {
    ...shorthands.padding("16px"),
    ...shorthands.borderRadius("8px"),
    ...shorthands.border("1px", "solid", "#E0E0E0"),
    backgroundColor: "#FFFFFF",
    boxShadow: "0 2px 8px rgba(11, 93, 93, 0.08), 0 1px 3px rgba(0, 0, 0, 0.06)",
  },
  title: {
    fontSize: "15px",
    fontWeight: "800",
    fontFamily: "'JetBrains Mono', monospace",
    color: "#0B5D5D",
    marginBottom: "0px",
    whiteSpace: "nowrap",
  },
  subtitle: {
    fontSize: "11px",
    color: "#666",
    lineHeight: "1.2",
    marginBottom: "0px",
  },
  formFields: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("12px"),
  },
  buttonContainer: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("12px"),
    marginTop: "8px",
  },
  submitButton: {
    backgroundColor: "#0B5D5D",
    color: "#ffffff",
    width: "100%",
    height: "42px",
    ":hover": {
      backgroundColor: "#094a4a",
    },
    ":active": {
      backgroundColor: "#063030",
    },
  },
  secondaryButton: {
    width: "100%",
    height: "42px",
  },
  fieldHint: {
    fontSize: "12px",
    color: "#666",
    marginTop: "4px",
  },
  tagContainer: {
    display: "flex",
    flexWrap: "wrap",
    ...shorthands.gap("6px"),
    marginTop: "8px",
  },
  tagDropdown: {
    maxHeight: "300px",
    overflowY: "hidden",
    ...shorthands.border("1px", "solid", "#E0E0E0"),
    ...shorthands.borderRadius("6px"),
    backgroundColor: "#FFFFFF",
    marginTop: "4px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
  },
  tagSearchInput: {
    ...shorthands.padding("8px", "12px"),
    ...shorthands.borderBottom("1px", "solid", "#E0E0E0"),
  },
  tagOption: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    ...shorthands.padding("8px", "12px"),
  },
  tagOptionLeft: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap("8px"),
    flex: 1,
  },
  tagCount: {
    backgroundColor: "#E6F4F4",
    color: "#0B5D5D",
    ...shorthands.padding("2px", "8px"),
    ...shorthands.borderRadius("12px"),
    fontSize: "12px",
    fontWeight: "600",
  },
  addTagButton: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap("6px"),
    ...shorthands.padding("10px", "12px"),
    color: "#0B5D5D",
    cursor: "pointer",
    ...shorthands.borderTop("1px", "solid", "#E0E0E0"),
    "&:hover": {
      backgroundColor: "#F5F5F5",
    },
  },
  addTagEditor: {
    padding: "12px",
    borderTop: "1px solid #E0E0E0",
    backgroundColor: "#FAFCFC",
  },
  addTagActionRow: {
    marginTop: "8px",
    display: "flex",
    gap: "8px",
  },
  addTagPrimaryButton: {
    backgroundColor: "#0B5D5D",
    color: "#FFFFFF",
    ":hover": {
      backgroundColor: "#094A4A",
    },
    ":active": {
      backgroundColor: "#073C3C",
    },
  },
  addTagSecondaryButton: {
    color: "#0B5D5D",
    border: "1px solid #BFD9D9",
    backgroundColor: "#FFFFFF",
    ":hover": {
      backgroundColor: "#F3FAFA",
    },
  },
  selectedTagChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    backgroundColor: "#E6F4F4",
    color: "#0B5D5D",
    padding: "4px 12px",
    borderRadius: "16px",
    fontSize: "13px",
    fontWeight: 500,
    border: "1px solid #BFE4E4",
  },
  selectedTagRemoveButton: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "0",
    display: "flex",
    alignItems: "center",
    color: "#0B5D5D",
    fontSize: "14px",
  },
  selectedTagsSection: {
    marginTop: "12px",
    ...shorthands.padding("12px"),
    backgroundColor: "#F8F8F8",
    ...shorthands.borderRadius("6px"),
  },
  blueTag: {
    backgroundColor: "#E3F2FD",
    color: "#1976D2",
    ...shorthands.border("none"),
    ...shorthands.padding("4px", "12px"),
    ...shorthands.borderRadius("16px"),
  },
  previewCard: {
    ...shorthands.padding("16px"),
    ...shorthands.borderRadius("6px"),
    boxShadow: "0 2px 8px rgba(25, 122, 86, 0.08), 0 1px 3px rgba(0, 0, 0, 0.06)",
    backgroundColor: "#F8F8F8",
  },
  previewTitle: {
    fontSize: "13px",
    fontWeight: "800",
    color: "#0B5D5D",
    marginBottom: "8px",
    display: "flex",
    alignItems: "center",
    ...shorthands.gap("6px"),
  },
  previewContainer: {
    backgroundColor: "#FFFFFF",
    ...shorthands.padding("12px"),
    ...shorthands.borderRadius("4px"),
    ...shorthands.border("1px", "solid", "#E0E0E0"),
    maxHeight: "300px",
    overflowY: "auto",
    fontFamily: "'Courier New', monospace",
    fontSize: "11px",
    lineHeight: "1.6",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  previewHint: {
    fontSize: "10px",
    color: "#666",
    fontStyle: "italic",
    marginBottom: "8px",
  },
  messageBar: {
    marginBottom: "16px",
  },
  successMessageBar: {
    marginBottom: "16px",
    backgroundColor: "#E6F4F4",
    ...shorthands.border("1px", "solid", "#57C1C1"),
  },
  dateInputWrapper: {
    display: "flex",
    alignItems: "center",
    position: "relative",
    width: "100%",
  },
  dateInput: {
    width: "100%",
  },
  dateIcon: {
    position: "absolute",
    right: "12px",
    cursor: "pointer",
    color: "#0B5D5D",
    fontSize: "18px",
    display: "flex",
    alignItems: "center",
    userSelect: "none",
  },
  deadlineInput: {
    width: "100%",
    paddingRight: "40px",
  },
  deadlineCalendarContainer: {
    position: "relative",
    width: "100%",
  },
  deadlineCalendarDropdown: {
    position: "absolute",
    top: "100%",
    marginTop: "4px",
    backgroundColor: "#FFFFFF",
    ...shorthands.border("1px", "solid", "#D0D0D0"),
    ...shorthands.borderRadius("6px"),
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    zIndex: 1000,
    padding: "8px",
    maxWidth: "280px",
    width: "max-content",
    left: 0,
    right: "auto",
  },
  calendarHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "8px",
    ...shorthands.borderBottom("1px", "solid", "#E0E0E0"),
    paddingBottom: "6px",
  },
  calendarNavButton: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#0B5D5D",
    fontSize: "12px",
    padding: "2px 4px",
    ":hover": {
      backgroundColor: "#F5F5F5",
    },
  },
  calendarGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: "1px",
  },
  calendarDay: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "26px",
    cursor: "pointer",
    ...shorthands.borderRadius("3px"),
    fontSize: "11px",
    border: "1px solid transparent",
    ":hover": {
      backgroundColor: "#E6F4F4",
    },
  },
  calendarDaySelected: {
    backgroundColor: "#0B5D5D",
    color: "#FFFFFF",
    fontWeight: "bold",
  },
  calendarDayOtherMonth: {
    color: "#CCC",
    cursor: "default",
    ":hover": {
      backgroundColor: "transparent",
    },
  },
  calendarDayHeader: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "24px",
    fontSize: "10px",
    fontWeight: "bold",
    color: "#0B5D5D",
  },
});

interface EmailSubmissionFormProps {
  defaultTitle: string;
  defaultLetterDate?: string;
  defaultDirection?: 'Received' | 'Sent';
  tenderTitle: string;
  tenderBaseCollectionName?: string; // Add this for tag fetching
  companyName?: string; // Add this for tag fetching
  senderName: string;
  senderEmail: string;
  recipientName: string;
  recipientEmail: string;
  emailBody: string;
  onSubmit: (formData: EmailFormData) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  isCheckingConversation: boolean;
  conversationCheckComplete: boolean;
  sendSuccess: boolean;
  sendError: string | null;
  isDuplicateMessage: boolean;
  isThreadMetadataMissing: boolean;
  threadMetadataError: string | null;
}

export const EmailSubmissionForm: React.FC<EmailSubmissionFormProps> = ({
  defaultTitle,
  defaultLetterDate,
  defaultDirection = 'Received',
  tenderTitle,
  tenderBaseCollectionName,
  companyName,
  senderName,
  senderEmail,
  recipientName,
  recipientEmail,
  emailBody,
  onSubmit,
  onCancel,
  isSubmitting,
  isCheckingConversation,
  conversationCheckComplete,
  sendSuccess,
  sendError,
  isDuplicateMessage,
  isThreadMetadataMissing,
  threadMetadataError,
}) => {
  const styles = useStyles();

  // Initialize form state
  const [title, setTitle] = useState(defaultTitle);
  const [direction, setDirection] = useState<'Received' | 'Sent'>(defaultDirection);
  const [letterDate, setLetterDate] = useState(() => {
    const rawDate = defaultLetterDate || new Date().toISOString();
    const parsed = new Date(rawDate);

    if (isNaN(parsed.getTime())) {
      return new Date().toISOString().split('T')[0];
    }

    // type="date" inputs require YYYY-MM-DD format.
    return parsed.toISOString().split('T')[0];
  });
  // Deadline should remain null/empty until user explicitly sets it (NOT auto-filled with letter date)
  const [deadline, setDeadline] = useState<string | null>(null);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [formattedPreview, setFormattedPreview] = useState("");

  // Tag autocomplete state
  const [tagInput, setTagInput] = useState("");
  const [availableTags, setAvailableTags] = useState<TagWithCount[]>([]);
  const [filteredTags, setFilteredTags] = useState<TagWithCount[]>([]);
  const [isLoadingTags, setIsLoadingTags] = useState(false);
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [isAddingNewTag, setIsAddingNewTag] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [showDeadlineCalendar, setShowDeadlineCalendar] = useState(false);
  const [showLetterDateCalendar, setShowLetterDateCalendar] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [letterDateCalendarMonth, setLetterDateCalendarMonth] = useState(() => {
    const parsed = new Date(letterDate);
    return isNaN(parsed.getTime()) ? new Date() : parsed;
  });
  const tagDropdownRef = useRef<HTMLDivElement>(null);
  const deadlineCalendarRef = useRef<HTMLDivElement>(null);
  const letterDateCalendarRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tagDropdownRef.current && !tagDropdownRef.current.contains(event.target as Node)) {
        setShowTagDropdown(false);
        setIsAddingNewTag(false);
      }
      if (deadlineCalendarRef.current && !deadlineCalendarRef.current.contains(event.target as Node)) {
        setShowDeadlineCalendar(false);
      }
      if (letterDateCalendarRef.current && !letterDateCalendarRef.current.contains(event.target as Node)) {
        setShowLetterDateCalendar(false);
      }
    };

    if (showTagDropdown || showDeadlineCalendar || showLetterDateCalendar) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTagDropdown, showDeadlineCalendar, showLetterDateCalendar]);

  // Fetch tags when component mounts
  useEffect(() => {
    const fetchTags = async () => {
      if (!companyName || !tenderBaseCollectionName) {
        console.log('[SUBMISSION_FORM] Skipping tag fetch - missing company or tender info');
        return;
      }

      setIsLoadingTags(true);
      try {
        const response = await tagService.fetchTags(companyName, tenderBaseCollectionName, 100);
        setAvailableTags(response.tags || []);
        setFilteredTags(response.tags || []);
        console.log(`[SUBMISSION_FORM] Loaded ${response.tags?.length || 0} tags`);
      } catch (error) {
        console.error('[SUBMISSION_FORM] Error fetching tags:', error);
        // Fail silently - tags are optional
      } finally {
        setIsLoadingTags(false);
      }
    };

    fetchTags();
  }, [companyName, tenderBaseCollectionName]);

  // Filter tags as user types
  const handleTagInputChange = useCallback((value: string) => {
    setTagInput(value);

    if (!value.trim()) {
      setFilteredTags(availableTags);
      return;
    }

    const queryLower = value.toLowerCase();
    const filtered = availableTags
      .filter(tag => tag.tag.toLowerCase().includes(queryLower))
      .sort((a, b) => {
        // Prioritize exact matches and starts-with matches
        const aStarts = a.tag.toLowerCase().startsWith(queryLower);
        const bStarts = b.tag.toLowerCase().startsWith(queryLower);
        if (aStarts && !bStarts) return -1;
        if (!aStarts && bStarts) return 1;
        return b.letter_count - a.letter_count; // Then sort by usage count
      });

    setFilteredTags(filtered);
  }, [availableTags]);

  // Add tag to selection
  const handleAddTag = useCallback((tag: string) => {
    if (tag && !selectedTags.includes(tag)) {
      setSelectedTags([...selectedTags, tag]);
      setTagInput("");
      setFilteredTags(availableTags);
      setShowTagDropdown(false);
    }
  }, [selectedTags, availableTags]);

  // Toggle tag selection (for checkbox)
  const handleToggleTag = useCallback((tag: string) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter(t => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  }, [selectedTags]);

  // Handle creating a new tag
  const handleCreateNewTag = useCallback(async () => {
    const trimmedTag = newTagName.trim();
    if (trimmedTag && !selectedTags.includes(trimmedTag)) {
      // Add tag to selected tags immediately
      setSelectedTags([...selectedTags, trimmedTag]);
      setNewTagName("");
      setIsAddingNewTag(false);

      // Insert tag to backend (if company and tender info available)
      if (companyName && tenderBaseCollectionName) {
        try {
          console.log(`[SUBMISSION_FORM] Inserting new tag: "${trimmedTag}"`);
          await tagService.insertTag(companyName, tenderBaseCollectionName, trimmedTag);

          // Refresh tags list to include the new tag
          const response = await tagService.fetchTags(companyName, tenderBaseCollectionName, 100);
          setAvailableTags(response.tags || []);
          setFilteredTags(response.tags || []);

          console.log(`[SUBMISSION_FORM] New tag created and list refreshed`);
        } catch (error) {
          console.error('[SUBMISSION_FORM] Error inserting new tag:', error);
          // Tag is still added to selected tags even if backend insert fails
        }
      }
    }
  }, [newTagName, selectedTags, companyName, tenderBaseCollectionName]);

  // Remove tag from selection
  const handleRemoveTag = useCallback((tagToRemove: string) => {
    setSelectedTags(selectedTags.filter(tag => tag !== tagToRemove));
  }, [selectedTags]);

  // Helper functions
  const stripHtml = (html: string): string => {
    // Remove HTML comments
    let cleaned = html.replace(/<!--[\s\S]*?-->/g, '');

    // Remove style tags and their content
    cleaned = cleaned.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');

    // Remove script tags and their content
    cleaned = cleaned.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');

    // Create a temporary div to extract text content
    const tmp = document.createElement("div");
    tmp.innerHTML = cleaned;

    // Get text content
    let text = tmp.textContent || tmp.innerText || "";

    // Clean up extra whitespace
    text = text.replace(/\s+/g, ' ').trim();

    // Replace multiple newlines with single newline
    text = text.replace(/\n\s*\n/g, '\n');

    return text;
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Format date for display in deadline field (dd/mm/yyyy)
  const formatDateForDisplay = (dateString: string | null): string => {
    if (!dateString) return "";
    try {
      const [year, month, day] = dateString.split('-');
      return `${day}/${month}/${year}`;
    } catch {
      return "";
    }
  };

  // Parse display format (dd/mm/yyyy) to internal format (yyyy-mm-dd)
  const parseDateFromDisplay = (displayDate: string): string | null => {
    if (!displayDate.trim()) return null;
    try {
      const parts = displayDate.split('/');
      if (parts.length !== 3) return null;
      const [day, month, year] = parts;
      return `${year}-${month}-${day}`;
    } catch {
      return null;
    }
  };

  // Generate formatted preview whenever form values change
  useEffect(() => {
    const cleanBody = stripHtml(emailBody);
    const formattedDate = formatDate(letterDate);

    let formatted = `Subject: ${title}\n`;
    formatted += `Project: ${tenderTitle}\n`;
    formatted += `From: ${senderName}`;
    if (senderEmail) {
      formatted += ` <${senderEmail}>`;
    }
    formatted += `\n`;
    formatted += `To: ${recipientName}`;
    if (recipientEmail) {
      formatted += ` <${recipientEmail}>`;
    }
    formatted += `\n`;
    formatted += `Date: ${formattedDate}\n`;

    if (deadline) {
      formatted += `Deadline: ${formatDate(deadline)}\n`;
    }

    if (selectedTags.length > 0) {
      formatted += `Tags: ${selectedTags.join(', ')}\n`;
    }

    formatted += `\n`;
    formatted += `${cleanBody}\n`;

    setFormattedPreview(formatted);
  }, [title, letterDate, deadline, selectedTags, direction, tenderTitle, senderName, senderEmail, recipientName, recipientEmail, emailBody]);

  const handleSubmit = () => {
    const letterTagsString = selectedTags.join(', ');

    console.log('[SUBMISSION_FORM] Submitting form data:', {
      title,
      direction,
      letterDate,
      deadline: deadline ?? 'not set',
      letterTags: letterTagsString,
      selectedTags,
      formattedText: formattedPreview
    });

    // Include formatted text in the submission
    onSubmit({
      title,
      direction,
      letterDate,
      deadline: deadline ?? undefined,
      letterTags: letterTagsString || undefined,
      formattedText: formattedPreview, // Add formatted text for .txt file generation
    } as any); // Type assertion since we're adding formattedText dynamically
  };

  const isFormValid = title.trim() !== "" && direction && letterDate;

  return (
    <div className={styles.root}>
      <Card className={styles.card}>
        <Text className={styles.title}>Email Submission Details</Text>
        <Text className={styles.subtitle}>(Preview and past letters shown below)</Text>

        <div className={styles.formFields}>
          <Field label={<Text weight="semibold">Title</Text>} required>
            <Input
              value={title}
              onChange={(e, data) => setTitle(data.value)}
              placeholder="Email title"
              disabled={isSubmitting}
            />
          </Field>

          <Field label={<Text weight="semibold">Direction</Text>} required>
            <Dropdown
              value={direction}
              selectedOptions={[direction]}
              onOptionSelect={(e, data) => setDirection(data.optionValue as 'Received' | 'Sent')}
              disabled={isSubmitting}
            >
              <Option value="Received">Received</Option>
              <Option value="Sent">Sent</Option>
            </Dropdown>
          </Field>

          <Field label={<Text weight="semibold">Letter Date</Text>} required>
            <div className={styles.deadlineCalendarContainer} ref={letterDateCalendarRef}>
              <div className={styles.dateInputWrapper}>
                <Input
                  type="text"
                  value={formatDateForDisplay(letterDate)}
                  placeholder="dd/mm/yyyy"
                  disabled={isSubmitting}
                  className={styles.deadlineInput}
                  style={{ paddingRight: "40px", cursor: "pointer" }}
                  onClick={() => {
                    if (!isSubmitting) {
                      setShowLetterDateCalendar(!showLetterDateCalendar);
                      setShowDeadlineCalendar(false);
                    }
                  }}
                />
                <CalendarRegular
                  className={styles.dateIcon}
                  onClick={() => {
                    if (!isSubmitting) {
                      setShowLetterDateCalendar(!showLetterDateCalendar);
                      setShowDeadlineCalendar(false);
                    }
                  }}
                />
              </div>

              {showLetterDateCalendar && !isSubmitting && (
                <div className={styles.deadlineCalendarDropdown}>
                  <div className={styles.calendarHeader}>
                    <button
                      className={styles.calendarNavButton}
                      onClick={() => setLetterDateCalendarMonth(new Date(letterDateCalendarMonth.getFullYear(), letterDateCalendarMonth.getMonth() - 1))}
                      type="button"
                    >
                      ← Prev
                    </button>
                    <Text weight="semibold" size={200} style={{ fontSize: "12px" }}>
                      {letterDateCalendarMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
                    </Text>
                    <button
                      className={styles.calendarNavButton}
                      onClick={() => setLetterDateCalendarMonth(new Date(letterDateCalendarMonth.getFullYear(), letterDateCalendarMonth.getMonth() + 1))}
                      type="button"
                    >
                      Next →
                    </button>
                  </div>

                  <div className={styles.calendarGrid}>
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                      <div key={day} className={styles.calendarDayHeader}>
                        {day}
                      </div>
                    ))}

                    {(() => {
                      const year = letterDateCalendarMonth.getFullYear();
                      const month = letterDateCalendarMonth.getMonth();
                      const firstDay = new Date(year, month, 1);
                      const startDate = new Date(firstDay);
                      startDate.setDate(startDate.getDate() - firstDay.getDay());

                      const days = [];
                      for (let i = 0; i < 42; i++) {
                        const date = new Date(startDate);
                        date.setDate(date.getDate() + i);

                        const isCurrentMonth = date.getMonth() === month;
                        const isSelected = date.toISOString().split('T')[0] === letterDate;

                        days.push(
                          <div
                            key={i}
                            className={`${styles.calendarDay} ${isSelected ? styles.calendarDaySelected : ''} ${!isCurrentMonth ? styles.calendarDayOtherMonth : ''}`}
                            onClick={() => {
                              if (isCurrentMonth) {
                                const y = date.getFullYear();
                                const m = String(date.getMonth() + 1).padStart(2, '0');
                                const d = String(date.getDate()).padStart(2, '0');
                                setLetterDate(`${y}-${m}-${d}`);
                                setShowLetterDateCalendar(false);
                              }
                            }}
                          >
                            {date.getDate()}
                          </div>
                        );
                      }
                      return days;
                    })()}
                  </div>
                </div>
              )}
            </div>
          </Field>

          <Field label={<Text weight="semibold">Deadline</Text>} hint="Optional - leave empty if no deadline">
            <div className={styles.deadlineCalendarContainer} ref={deadlineCalendarRef}>
              {/* Display field */}
              <div className={styles.dateInputWrapper}>
                <Input
                  type="text"
                  value={formatDateForDisplay(deadline)}
                  placeholder="dd/mm/yyyy"
                  disabled={isSubmitting}
                  className={styles.deadlineInput}
                  style={{ paddingRight: "40px", cursor: "pointer" }}
                  onClick={() => !isSubmitting && setShowDeadlineCalendar(!showDeadlineCalendar)}
                />
                <CalendarRegular
                  className={styles.dateIcon}
                  onClick={() => !isSubmitting && setShowDeadlineCalendar(!showDeadlineCalendar)}
                />
              </div>

              {/* Calendar dropdown */}
              {showDeadlineCalendar && !isSubmitting && (
                <div className={styles.deadlineCalendarDropdown}>
                  {/* Calendar header */}
                  <div className={styles.calendarHeader}>
                    <button
                      className={styles.calendarNavButton}
                      onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1))}
                      type="button"
                    >
                      ← Prev
                    </button>
                    <Text weight="semibold" size={200} style={{ fontSize: "12px" }}>
                      {calendarMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
                    </Text>
                    <button
                      className={styles.calendarNavButton}
                      onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1))}
                      type="button"
                    >
                      Next →
                    </button>
                  </div>

                  {/* Day headers */}
                  <div className={styles.calendarGrid}>
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                      <div key={day} className={styles.calendarDayHeader}>
                        {day}
                      </div>
                    ))}

                    {/* Calendar days */}
                    {(() => {
                      const year = calendarMonth.getFullYear();
                      const month = calendarMonth.getMonth();
                      const firstDay = new Date(year, month, 1);
                      const lastDay = new Date(year, month + 1, 0);
                      const startDate = new Date(firstDay);
                      startDate.setDate(startDate.getDate() - firstDay.getDay());

                      const days = [];
                      for (let i = 0; i < 42; i++) {
                        const date = new Date(startDate);
                        date.setDate(date.getDate() + i);

                        const isCurrentMonth = date.getMonth() === month;
                        const isSelected = deadline && date.toISOString().split('T')[0] === deadline;

                        days.push(
                          <div
                            key={i}
                            className={`${styles.calendarDay} ${isSelected ? styles.calendarDaySelected : ''} ${!isCurrentMonth ? styles.calendarDayOtherMonth : ''}`}
                            onClick={() => {
                              if (isCurrentMonth) {
                                const year = date.getFullYear();
                                const month = String(date.getMonth() + 1).padStart(2, '0');
                                const day = String(date.getDate()).padStart(2, '0');
                                setDeadline(`${year}-${month}-${day}`);
                                setShowDeadlineCalendar(false);
                              }
                            }}
                          >
                            {date.getDate()}
                          </div>
                        );
                      }
                      return days;
                    })()}
                  </div>

                  {/* Clear button */}
                  {deadline && (
                    <Button
                      size="small"
                      appearance="secondary"
                      onClick={() => {
                        setDeadline(null);
                        setShowDeadlineCalendar(false);
                      }}
                      style={{ width: "100%", marginTop: "6px", fontSize: "11px" }}
                    >
                      Clear
                    </Button>
                  )}
                </div>
              )}
            </div>
          </Field>

          <Field label={<Text weight="semibold">Letter Tags</Text>} hint="Optional - select from dropdown or type to create new">
            <div style={{ position: "relative" }} ref={tagDropdownRef}>
              {/* Dropdown appears above the input */}
              {showTagDropdown && (
                <div className={styles.tagDropdown} style={{ position: "absolute", bottom: "100%", marginBottom: "4px", width: "100%", zIndex: 1000 }}>
                  {/* Tag list at top */}
                  <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                    {isLoadingTags ? (
                      <div style={{ padding: "12px", textAlign: "center", color: "#666" }}>
                        Loading tags...
                      </div>
                    ) : filteredTags.length > 0 ? (
                      filteredTags.map((tagObj) => (
                        <div
                          key={tagObj.tag}
                          className={styles.tagOption}
                        >
                          <div className={styles.tagOptionLeft}>
                            <Checkbox
                              checked={selectedTags.includes(tagObj.tag)}
                              onChange={() => handleToggleTag(tagObj.tag)}
                            />
                            <Text style={{ cursor: "pointer" }} onClick={() => handleToggleTag(tagObj.tag)}>
                              {tagObj.tag}
                            </Text>
                          </div>
                          <div className={styles.tagCount}>{tagObj.letter_count}</div>
                        </div>
                      ))
                    ) : (
                      <div style={{ padding: "12px", textAlign: "center", color: "#666" }}>
                        {tagInput ? `No tags found for "${tagInput}"` : "No tags available"}
                      </div>
                    )}
                  </div>

                  {/* Add new tag section in the middle */}
                  {!isAddingNewTag ? (
                    <div
                      className={styles.addTagButton}
                      onClick={() => setIsAddingNewTag(true)}
                    >
                      <AddRegular fontSize={16} />
                      <Text style={{ color: "#0B5D5D", fontWeight: 500 }}>Add a New Tag</Text>
                    </div>
                  ) : (
                    <div className={styles.addTagEditor}>
                      <Input
                        placeholder="Enter new tag name..."
                        value={newTagName}
                        onChange={(e, data) => setNewTagName(data.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleCreateNewTag();
                          } else if (e.key === 'Escape') {
                            setIsAddingNewTag(false);
                            setNewTagName("");
                          }
                        }}
                        autoFocus
                      />
                      <div className={styles.addTagActionRow}>
                        <Button
                          size="small"
                          appearance="primary"
                          className={styles.addTagPrimaryButton}
                          onClick={handleCreateNewTag}
                          disabled={!newTagName.trim()}
                        >
                          Add Tag
                        </Button>
                        <Button
                          size="small"
                          appearance="secondary"
                          className={styles.addTagSecondaryButton}
                          onClick={() => {
                            setIsAddingNewTag(false);
                            setNewTagName("");
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                  {/* Removed redundant Search bar at bottom of dropdown */}
                </div>
              )}

              {/* Main input field - now used for searching */}
              <Input
                placeholder={isLoadingTags ? "Loading tags..." : "Search and select tags"}
                value={tagInput}
                onChange={(e, data) => {
                  setTagInput(data.value);
                  handleTagInputChange(data.value);
                  if (!showTagDropdown) setShowTagDropdown(true);
                }}
                onFocus={() => setShowTagDropdown(true)}
                disabled={isSubmitting || isLoadingTags}
                contentBefore={<SearchRegular />}
                style={{ width: "100%" }}
              />

              {/* Show selected tags as blue chips below */}
              {selectedTags.length > 0 && (
                <div style={{ marginTop: "8px", display: "flex", flexWrap: "wrap", gap: "6px" }}>
                  {selectedTags.map((tag) => (
                    <div
                      key={tag}
                      className={styles.selectedTagChip}
                    >
                      <span>{tag}</span>
                      <button
                        onClick={() => handleRemoveTag(tag)}
                        className={styles.selectedTagRemoveButton}
                        title="Remove tag"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Field>
        </div>

        {/* Messages above button */}
        {isThreadMetadataMissing && threadMetadataError && (
          <MessageBar intent="error" className={styles.messageBar}>
            <MessageBarBody>
              {threadMetadataError}
            </MessageBarBody>
          </MessageBar>
        )}

        {/* Grey warning for duplicate - also shown at bottom */}
        {isDuplicateMessage && !sendSuccess && !sendError && (
          <MessageBar intent="warning" className={styles.messageBar} style={{
            backgroundColor: "#f5f5f5",
            borderColor: "#ccc"
          }}>
            <MessageBarBody>
              <div style={{ color: "#666" }}>This email has already been submitted.</div>
            </MessageBarBody>
          </MessageBar>
        )}

        {/* Success message */}
        {sendSuccess && !sendError && (
          <MessageBar className={styles.successMessageBar}>
            <MessageBarBody style={{ color: "#0B5D5D" }}>
              Email sent to ContraVault AI successfully.
            </MessageBarBody>
          </MessageBar>
        )}

        {/* Error message */}
        {sendError && !sendSuccess && (
          <MessageBar intent="error" className={styles.messageBar}>
            <MessageBarBody>
              {sendError}
            </MessageBarBody>
          </MessageBar>
        )}

        <div className={styles.buttonContainer}>
          <Button
            appearance="primary"
            className={styles.submitButton}
            onClick={handleSubmit}
            disabled={
              isSubmitting ||
              isCheckingConversation ||
              !conversationCheckComplete ||
              !isFormValid ||
              sendSuccess ||
              isDuplicateMessage ||
              isThreadMetadataMissing
            }
          >
            {isSubmitting ? "Submitting..." : "Submit to ContraVault AI"}
          </Button>
          <Button
            appearance="secondary"
            className={styles.secondaryButton}
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Back to Tender Selection
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default EmailSubmissionForm;
